import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  AlertTriangle, Building2, Calendar, FileText, Send, CheckCircle2,
  Clock, AlertCircle, Loader2, XCircle, Paperclip, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatDate } from '@/utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_COLORS = {
  Low: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  Medium: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  Critical: 'bg-red-500/20 text-red-400 border-red-500/30'
};

const ExternalNcrView = () => {
  const { token } = useParams();
  const [ncr, setNcr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [response, setResponse] = useState({
    response_text: '',
    corrective_action: '',
    preventive_action: '',
    target_completion_date: '',
    submitted_by: ''
  });

  useEffect(() => {
    fetchNcr();
  }, [token]);

  const fetchNcr = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/external/ncr/${token}`);
      setNcr(res.data);
      
      // Pre-fill if there's an existing response
      if (res.data.supplier_response) {
        setResponse({
          response_text: res.data.supplier_response.response_text || '',
          corrective_action: res.data.supplier_response.corrective_action || '',
          preventive_action: res.data.supplier_response.preventive_action || '',
          target_completion_date: res.data.supplier_response.target_completion_date?.split('T')[0] || '',
          submitted_by: res.data.supplier_response.submitted_by || ''
        });
        setSubmitted(true);
      }
    } catch (err) {
      console.error('Error fetching NCR:', err);
      if (err.response?.status === 410) {
        setError('This link has expired. Please contact the company for a new link.');
      } else if (err.response?.status === 404) {
        setError('Invalid link. This NCR access link is not valid.');
      } else {
        setError('Failed to load NCR details. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!response.response_text.trim()) {
      toast.error('Please provide a response');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/external/ncr/${token}/respond`, {
        ...response,
        target_completion_date: response.target_completion_date 
          ? new Date(response.target_completion_date).toISOString() 
          : null
      });
      
      toast.success('Response submitted successfully');
      setSubmitted(true);
      fetchNcr(); // Refresh to show updated data
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-cyan-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading NCR details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="bg-slate-900 border border-red-500/30 rounded-lg p-8 max-w-md text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Access Error</h1>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900 border-b border-cyan-500/20 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Non-Conformance Report</h1>
              <p className="text-xs text-slate-500">Supplier Response Portal</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-cyan-400 font-mono font-bold">{ncr.ncr_number}</p>
            <p className="text-xs text-slate-500">{ncr.supplier_name}</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6">
        {/* NCR Details */}
        <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-white uppercase tracking-wide mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-400" />
            NCR Details
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <span className="text-xs text-slate-500 uppercase">Type</span>
              <p className="text-white font-medium">{ncr.ncr_type}</p>
            </div>
            <div>
              <span className="text-xs text-slate-500 uppercase">Severity</span>
              <p className={`inline-block px-2 py-0.5 rounded text-sm font-bold ${SEVERITY_COLORS[ncr.severity]}`}>
                {ncr.severity}
              </p>
            </div>
            <div>
              <span className="text-xs text-slate-500 uppercase">Status</span>
              <p className="text-cyan-400 font-medium">{ncr.status}</p>
            </div>
            <div>
              <span className="text-xs text-slate-500 uppercase">Date Raised</span>
              <p className="text-white">{formatDate(ncr.date_raised)}</p>
            </div>
            <div>
              <span className="text-xs text-slate-500 uppercase">Department</span>
              <p className="text-white">{ncr.department || '-'}</p>
            </div>
            <div>
              <span className="text-xs text-slate-500 uppercase">Reference</span>
              <p className="text-white">{ncr.work_order_reference || ncr.customer_reference || '-'}</p>
            </div>
          </div>

          <div className="mb-4">
            <span className="text-xs text-slate-500 uppercase">Description of Non-Conformance</span>
            <p className="text-white mt-1 bg-slate-800/50 rounded p-3 whitespace-pre-wrap">
              {ncr.description}
            </p>
          </div>

          {ncr.containment?.action && (
            <div className="mb-4">
              <span className="text-xs text-slate-500 uppercase">Containment Action Taken</span>
              <p className="text-white mt-1 bg-slate-800/50 rounded p-3">
                {ncr.containment.action}
              </p>
            </div>
          )}

          {/* Attachments */}
          {ncr.attachments && ncr.attachments.length > 0 && (
            <div>
              <span className="text-xs text-slate-500 uppercase flex items-center gap-1 mb-2">
                <Paperclip className="w-3 h-3" />
                Attachments ({ncr.attachments.length})
              </span>
              <div className="flex flex-wrap gap-2">
                {ncr.attachments.map((att) => (
                  <a
                    key={att.attachment_id}
                    href={`${BACKEND_URL}/api/ncrs/${ncr.ncr_number}/attachments/${att.attachment_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 bg-slate-800 rounded border border-slate-700 hover:border-cyan-500/50 transition-colors"
                  >
                    <Download className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm text-white">{att.original_filename}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Supplier Response Section */}
        <div className="bg-slate-900/60 border border-amber-500/30 rounded-lg p-6">
          <h2 className="text-lg font-bold text-white uppercase tracking-wide mb-4 flex items-center gap-2">
            <Send className="w-5 h-5 text-amber-400" />
            Supplier Response
            {submitted && (
              <span className="ml-2 text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                Submitted
              </span>
            )}
          </h2>

          {!ncr.can_respond ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-slate-400">This NCR has been closed and is no longer accepting responses.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Your Name / Company *</Label>
                <Input
                  value={response.submitted_by}
                  onChange={(e) => setResponse({ ...response, submitted_by: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="Your name or company"
                />
              </div>

              <div>
                <Label className="text-slate-400 uppercase text-xs">Response / Acknowledgment *</Label>
                <Textarea
                  value={response.response_text}
                  onChange={(e) => setResponse({ ...response, response_text: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="Your response to this non-conformance..."
                  rows={3}
                />
              </div>

              <div>
                <Label className="text-slate-400 uppercase text-xs">Corrective Action</Label>
                <Textarea
                  value={response.corrective_action}
                  onChange={(e) => setResponse({ ...response, corrective_action: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="What corrective action will be taken to address this issue?"
                  rows={3}
                />
              </div>

              <div>
                <Label className="text-slate-400 uppercase text-xs">Preventive Action</Label>
                <Textarea
                  value={response.preventive_action}
                  onChange={(e) => setResponse({ ...response, preventive_action: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="What preventive action will be taken to prevent recurrence?"
                  rows={3}
                />
              </div>

              <div className="w-48">
                <Label className="text-slate-400 uppercase text-xs">Target Completion Date</Label>
                <Input
                  type="date"
                  value={response.target_completion_date}
                  onChange={(e) => setResponse({ ...response, target_completion_date: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>

              <div className="pt-4">
                <Button 
                  onClick={handleSubmit} 
                  disabled={submitting}
                  className="bg-amber-600 hover:bg-amber-500 text-white"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {submitted ? 'Update Response' : 'Submit Response'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Previous Response (if exists) */}
        {ncr.supplier_response && (
          <div className="mt-6 bg-slate-900/60 border border-green-500/30 rounded-lg p-6">
            <h3 className="text-sm font-bold text-green-400 uppercase mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Response Submitted on {formatDate(ncr.supplier_response.submitted_at)}
            </h3>
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-slate-500">Submitted By:</span>
                <span className="text-white ml-2">{ncr.supplier_response.submitted_by}</span>
              </div>
              {ncr.supplier_response.response_text && (
                <div>
                  <span className="text-slate-500">Response:</span>
                  <p className="text-white mt-1">{ncr.supplier_response.response_text}</p>
                </div>
              )}
              {ncr.supplier_response.corrective_action && (
                <div>
                  <span className="text-slate-500">Corrective Action:</span>
                  <p className="text-white mt-1">{ncr.supplier_response.corrective_action}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-slate-600 text-xs">
          <p>Hotforge AI-Workflow • Supplier NCR Portal</p>
        </div>
      </main>
    </div>
  );
};

export default ExternalNcrView;
